import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigationLinks, businessInfo } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const isActive = (path) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-brand-sage-medium/20 bg-brand-beige/95 backdrop-blur-md transition-all duration-300">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:py-5">
        {/* Logo */}
        <Link 
          to="/" 
          className="group flex items-center gap-3 focus:outline-none relative z-50"
          aria-label="Stausbroe Bowen Terapi Forside"
        >
          <div className="flex flex-col">
            <span className="font-serif text-lg font-semibold tracking-wide text-brand-neutral-dark md:text-xl">
              Stausbroe
            </span>
            <span className="text-xs tracking-[0.15em] uppercase text-brand-neutral-light">
              Bowen Terapi
            </span>
          </div>
        </Link>

        {/* Desktop Navigation (Visible at 1200px and above) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Hovednavigation">
          {navigationLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-brand-sage-dark ${
                isActive(link.path)
                  ? 'text-brand-sage-dark font-semibold'
                  : 'text-brand-neutral-dark/80'
              }`}
            >
              {link.label}
              {isActive(link.path) && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-sage" />
              )}
            </Link>
          ))}
          
          {/* CTA Button */}
          <Link
            to="/kontakt"
            className="ml-4 inline-flex items-center justify-center rounded-full bg-brand-sage px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-sage focus:ring-offset-2"
          >
            Book tid
          </Link>
        </nav>

        {/* Mobile Menu Button (Visible below 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-sage-medium/30 text-brand-neutral-dark hover:bg-brand-sage-light focus:outline-none min-[1200px]:hidden relative z-50"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Luk menu' : 'Åbn menu'}
        >
          <i className={`fa-light ${isOpen ? 'fa-xmark text-lg' : 'fa-bars text-lg'}`} />
        </button>
      </div>

      {/* Mobile Navigation Overlay (Visible below 1200px) */}
      {isOpen && (
        <div className="fixed inset-0 h-screen w-screen z-40 flex flex-col bg-white overflow-y-auto pt-28 pb-12 px-6 min-[1200px]:hidden animate-in fade-in slide-in-from-top-5 duration-300">
          <nav className="flex flex-col gap-6 text-center my-auto" aria-label="Mobilnavigation">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-medium tracking-wide py-2 border-b border-brand-sage-medium/10 ${
                  isActive(link.path)
                    ? 'text-brand-sage-dark font-bold'
                    : 'text-brand-neutral-dark/80'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            <Link
              to="/kontakt"
              className="mt-4 inline-flex items-center justify-center rounded-full bg-brand-sage py-3 text-base font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark"
            >
              Book tid
            </Link>
          </nav>

          {/* Mobile Menu Footer Info */}
          <div className="mt-auto text-center border-t border-brand-sage-medium/20 pt-8">
            <p className="text-sm font-medium text-brand-neutral-dark">Anita Zacho</p>
            <p className="text-xs text-brand-neutral-light mt-1">
              <i className="fa-light fa-phone mr-1.5" />
              <span className="whitespace-nowrap">{businessInfo.phone}</span>
            </p>
            {businessInfo.email && (
              <p className="text-xs text-brand-neutral-light mt-1">
                <i className="fa-light fa-envelope mr-1.5" />
                <span className="whitespace-nowrap">{businessInfo.email}</span>
              </p>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
